import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from "victory";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import lightColors from "assets/theme/base/colors";
import darkColors from "assets/theme-dark/base/colors";
import helpers from "../AnalyticsHelpers";

function AnalyticsComparisonChart({ charts, darkMode, metricsConfig, queryPeriod, t }) {
  const activeColors = darkMode ? darkColors : lightColors;
  const chartModeColors = darkMode
    ? {
        cardBorder: activeColors.background.card,
        cardBackground: activeColors.background.card,
        titleText: activeColors.white.main,
        legendText: activeColors.white.main,
        axis: activeColors.white.main,
        grid: activeColors.dark.focus,
        tooltipFill: activeColors.background.card,
        tooltipStroke: activeColors.dark.focus,
        tooltipText: activeColors.white.main,
      }
    : {
        cardBorder: activeColors.grey[300],
        cardBackground: activeColors.white.main,
        titleText: activeColors.dark.main,
        legendText: activeColors.text.main,
        axis: activeColors.grey[600],
        grid: activeColors.grey[300],
        tooltipFill: activeColors.white.main,
        tooltipStroke: activeColors.grey[300],
        tooltipText: activeColors.dark.main,
      };
  const comparisonSeriesColors = [activeColors.info.main, activeColors.success.main];

  const wrapRef = useRef(null);
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(380);

  useEffect(() => {
    if (!wrapRef.current || typeof ResizeObserver === "undefined") {
      return () => {};
    }

    const ro = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      if (w) setWidth(w);
      if (h) setHeight(Math.max(320, h - 90));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const { timeline, tickMap, seriesList, yDomain } = useMemo(
    () =>
      helpers.buildComparisonChartModel({
        charts,
        metricsConfig,
        seriesColors: comparisonSeriesColors,
      }),
    [charts, metricsConfig]
  );

  if (!timeline.length || !seriesList.length) return null;

  const tickValues = timeline.map((_, idx) => idx + 1);
  const xDomainPadding = helpers.getXAxisDomainPadding(timeline.length);
  const yDomainWithVisualOffset = [yDomain[0], yDomain[1] + helpers.getMaxSeriesVisualYOffset()];

  const baseColor = activeColors.info.main;

  return (
    <MDBox sx={{ width: "100%", overflowX: "auto", overflowY: "hidden", mt: 1, mb: 1 }}>
      <MDBox
        style={{
          borderRadius: 12,
          padding: 10,
          border: `1px solid ${chartModeColors.cardBorder}`,
          background: chartModeColors.cardBackground,
          width: Math.max(width, timeline.length * 60),
          minWidth: "800px",
          height: "500px",
        }}
        ref={wrapRef}
      >
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="inherit"
          style={{ color: chartModeColors.titleText }}
        >
          {t("compareCharts")}
        </MDTypography>

        <MDBox
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            marginTop: 4,
            marginBottom: 6,
          }}
        >
          {seriesList.map((serie) => (
            <MDBox
              key={serie.id}
              style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 150 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: serie.color,
                  display: "inline-block",
                }}
              />
              <MDTypography
                variant="caption"
                color="inherit"
                style={{ color: chartModeColors.legendText }}
              >
                {serie.label}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>

        <VictoryChart
          width={width}
          height={height}
          padding={{ top: 40, bottom: 76, left: 70, right: 40 }}
          domain={{ y: yDomainWithVisualOffset }}
          domainPadding={{ x: xDomainPadding, y: 15 }}
          animate={{ duration: 400 }}
          containerComponent={<VictoryContainer activateData />}
        >
          <VictoryAxis
            dependentAxis
            style={{
              axis: { stroke: chartModeColors.axis },
              grid: { stroke: chartModeColors.grid },
              tickLabels: { fill: activeColors.transparent.main },
            }}
            tickFormat={() => ""}
          />

          <VictoryAxis
            tickValues={tickValues}
            tickFormat={(tickValue) => tickMap[tickValue]}
            style={{
              axis: { stroke: chartModeColors.axis },
              tickLabels: {
                fill: chartModeColors.axis,
                fontSize: 12,
                angle: -12,
                textAnchor: "end",
              },
            }}
          />

          {seriesList.map((serie, seriesIdx) =>
            helpers.isPresenceBinarySeries(serie) ? null : (
              <VictoryLine
                key={`${serie.id}-line`}
                data={serie.data}
                y={({ y }) => helpers.getRenderedYValue(y, seriesIdx)}
                style={{ data: { stroke: serie.color, strokeWidth: 3 } }}
              />
            )
          )}

          {seriesList.map((serie, seriesIdx) =>
            helpers.isPresenceBinarySeries(serie) ? (
              <VictoryBar
                key={`${serie.id}-bars`}
                data={serie.data}
                x="barX"
                y={({ y }) => helpers.getRenderedYValue(y, seriesIdx)}
                barWidth={Math.max(6, Math.min(width / Math.max(1, timeline.length) / 3, 18))}
                style={{
                  data: {
                    fill: ({ datum }) => {
                      const showStateInTooltip = helpers.shouldShowStateInComparisonTooltip({
                        queryPeriod,
                        datum,
                      });

                      if (helpers.isRecoverTooltipState({ toolTip: datum, showStateInTooltip })) {
                        return activeColors.warning.main;
                      }

                      return datum.pointValue === 0 ? activeColors.error.main : baseColor;
                    },
                  },
                }}
                labels={({ datum }) =>
                  helpers.buildComparisonTooltipLabel({
                    datum,
                    queryPeriod,
                    t,
                  })
                }
                labelComponent={
                  <VictoryTooltip
                    flyoutStyle={{
                      fill: chartModeColors.tooltipFill,
                      stroke: chartModeColors.tooltipStroke,
                      strokeWidth: 1,
                    }}
                    flyoutPadding={6}
                    pointerLength={4}
                    constrainToVisibleArea
                    style={{
                      fontSize: 14,
                      fill: chartModeColors.tooltipText,
                    }}
                  />
                }
              />
            ) : (
              <VictoryScatter
                key={`${serie.id}-dots`}
                data={serie.data}
                y={({ y }) => helpers.getRenderedYValue(y, seriesIdx)}
                size={3}
                style={{
                  data: {
                    fill: ({ datum }) => {
                      const showStateInTooltip = helpers.shouldShowStateInComparisonTooltip({
                        queryPeriod,
                        datum,
                      });

                      return helpers.isRecoverTooltipState({ toolTip: datum, showStateInTooltip })
                        ? activeColors.warning.main
                        : serie.color;
                    },
                  },
                }}
                labels={({ datum }) =>
                  helpers.buildComparisonTooltipLabel({
                    datum,
                    queryPeriod,
                    t,
                  })
                }
                labelComponent={
                  <VictoryTooltip
                    flyoutStyle={{
                      fill: chartModeColors.tooltipFill,
                      stroke: chartModeColors.tooltipStroke,
                      strokeWidth: 1,
                    }}
                    flyoutPadding={6}
                    pointerLength={4}
                    constrainToVisibleArea
                    style={{
                      fontSize: 14,
                      fill: chartModeColors.tooltipText,
                    }}
                  />
                }
              />
            )
          )}
        </VictoryChart>
      </MDBox>
    </MDBox>
  );
}

AnalyticsComparisonChart.propTypes = {
  charts: PropTypes.arrayOf(PropTypes.object).isRequired,
  darkMode: PropTypes.bool,
  metricsConfig: PropTypes.object,
  queryPeriod: PropTypes.string,
  t: PropTypes.func,
};

AnalyticsComparisonChart.defaultProps = {
  darkMode: false,
  metricsConfig: {},
  queryPeriod: "hourly",
  t: () => {},
};

export default AnalyticsComparisonChart;
